/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Download, ShareButton } from 'components/Download';
import { useIntl } from 'react-intl';

/**
 * 🗓️ Muestra los tiempos de operación (activa/inactiva) para cada día del mes.
 *
 * @param {object} props - Las propiedades del componente.
 * @param {object} props.onOff - Objeto que contiene los tiempos de encendido y apagado para cada día.
 * @param {number} props.year - El año a mostrar.
 * @param {number} props.month - El mes a mostrar.
 * @returns {JSX.Element} Un componente que muestra los tiempos de operación.
 */
export default function OperationTime({ onOff, year, month }) {
  const intl = useIntl();
  // 📅 Crea un array con los días del mes
  const [days, setDays] = useState([]);
  const localeNav = useSelector((state) => state.prefs.locale);
  const tableRef = useRef();

  useEffect(() => {
    // 🔢 Calcula el número de días en el mes.
    const daysInMonth = year
      ? moment(`${year}${month}`).daysInMonth()
      : moment().daysInMonth();
    const currentMonth = moment().month();
    const currentYear = moment().year();
    const daysArray = [];

    // 🌍 Configura la localización de moment.js
    moment.locale(localeNav);

    // 🔄 Itera sobre cada día del mes.
    for (let i = 1; i <= daysInMonth; i++) {
      const date = moment({
        year: year ? year : currentYear,
        month: month ? month - 1 : currentMonth,
        day: i,
      });
      daysArray.push({
        day: i,
        // 🔤 Obtiene el día de la semana abreviado.
        weekday: date.format('ddd'),
      });
    }

    // 📥 Actualiza el estado con el array de días.
    setDays(daysArray);
  }, [localeNav, month, year]);

  return (
    <Paper sx={{ p: 2, overflowX: 'hidden', overflowY: 'auto' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Chip
          label={intl.formatMessage({
            id: 'app.charge.board.OperationTime.title',
          })}
          sx={{
            fontWeight: 700,
            backgroundColor: '#730065',
            fontSize: '16px',
            color: 'white',
          }}
        />
        <Box display="flex" gap={1}>
          <Download
            itemRef={tableRef}
            name={intl.formatMessage({
              id: 'app.charge.board.OperationTime.title',
            })}
          />
          <ShareButton
            itemRef={tableRef}
            name={intl.formatMessage({
              id: 'app.charge.board.OperationTime.title',
            })}
          />
        </Box>
      </Box>
      <Box overflow="auto" ref={tableRef} pt={3}>
        <Table size="small" sx={{ width: '100%' }}>
          <TableHead>
            {/* 📄 Muestra la cabecera de la tabla si hay datos disponibles */}
            {onOff !== undefined && onOff !== null ? (
              <TableRow>
                <TableCell>
                  {intl.formatMessage({
                    id: 'app.charge.board.OperationTime.status',
                  })}
                </TableCell>
                {days.map((day, index) => (
                  <TableCell
                    key={index}
                    align="center"
                    padding="normal"
                    sx={{ borderBottom: '1px solid #ccc' }}
                  >
                    {day.day}
                    <br />
                    {/* 🔡 Formatea el día de la semana */}
                    {day.weekday.charAt(0).toUpperCase() +
                      day.weekday.slice(1, 3)}
                  </TableCell>
                ))}
              </TableRow>
            ) : null}
          </TableHead>
          <TableBody sx={{ padding: '0 !important' }}>
            {/* 🟢 Muestra los tiempos de actividad */}
            {onOff !== undefined && onOff !== null ? (
              <TableRow sx={{ height: '0px' }}>
                <TableCell component="th" scope="row" align="center">
                  {intl.formatMessage({
                    id: 'app.charge.board.OperationTime.active',
                  })}
                </TableCell>
                {Object.values(onOff).map((item, index) => (
                  <TableCell
                    component="th"
                    key={index}
                    scope="row"
                    align="center"
                    sx={{ borderBottom: '1px solid #ccc', p: 1.2 }}
                  >
                    {/* ⏰ Formatea la hora de activación */}
                    {item
                      ? item.on > 9
                        ? `${item.on}:00`
                        : `0${item.on}:00`
                      : '00:00'}
                  </TableCell>
                ))}
              </TableRow>
            ) : null}
            {/* 🔴 Muestra los tiempos de inactividad */}
            {onOff !== undefined && onOff !== null ? (
              <TableRow sx={{ height: '0px' }}>
                <TableCell component="th" scope="row" align="center">
                  {intl.formatMessage({
                    id: 'app.charge.board.OperationTime.inactive',
                  })}
                </TableCell>
                {Object.values(onOff).map((item, index) => (
                  <TableCell
                    component="th"
                    key={index}
                    scope="row"
                    align="center"
                    sx={{ borderBottom: '1px solid #ccc', p: 1.2 }}
                  >
                    {/* ⏰ Formatea la hora de desactivación */}
                    {item
                      ? item.off > 9
                        ? `${item.off}:00`
                        : `0${item.off}:00`
                      : '00:00'}
                  </TableCell>
                ))}
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}
